import * as Y from "yjs";
import { teileSchluessel, zellschluessel } from "./zellbezug";
import { verschiebeFormel } from "./formelverschieber";

/*
  Das Modell einer Tabelle.

  Eine Tabelle ist kein eigener Datensatz auf dem Server — sie ist ein
  `dokument` mit `art = "tabelle"`. Dieselbe Sammlung, dieselben
  Zugriffsregeln, dieselbe Echtzeit wie Textdokument und Präsentation. Nur
  der Inhalt im Yjs-Dokument sieht anders aus:

      Y.Array "spalten"  →  Y.Map { id, reihenfolge, format, breite }
      Y.Array "zeilen"   →  Y.Map { id, reihenfolge }
      Y.Map   "zellen"   →  "sp-…|z-…"  →  "45000" | "=SUMME(B2:B4)"

  ---------------------------------------------------------------
  Warum drei Dinge nebeneinander und nicht ineinander
  ---------------------------------------------------------------

  `doc.getArray(name)` liefert immer dieselbe Instanz — auch bevor
  überhaupt Inhalt angekommen ist. Der Rückgängig-Verwalter lässt sich damit
  sofort bauen. Steckten die Listen in einer übergeordneten Map, gäbe es sie
  vor Eintreffen des Gerüsts noch gar nicht. Genau deshalb liegt auch
  `folien` bei den Präsentationen oben.

  ---------------------------------------------------------------
  Warum die Zellen flach liegen und nur die gefüllten
  ---------------------------------------------------------------

  Nicht Zeile für Zeile verschachtelt, sondern eine flache Map mit einem
  Schlüssel aus zwei Kennungen. Das hat drei Folgen, die alle erwünscht sind:

    Eine Zelle zu schreiben ist EIN Vorgang. Zwei Leute in verschiedenen
    Zellen stören einander nie; zwei in derselben — der letzte gewinnt.

    Ein leeres Blatt kostet fast nichts. Nur gefüllte Zellen existieren.

    Eine Spalte einzufügen fasst keine einzige Zelle an. Die Position ist
    eine Ableitung aus der Sortierung, kein gespeicherter Wert.

  ---------------------------------------------------------------
  Warum Zellen Zeichenketten sind und keine Y.Text
  ---------------------------------------------------------------

  Naheliegend wäre `Y.Text` je Zelle, wie in den Folienkästen. Hier wäre es
  falsch: Kommentare hängen an der ZELLE, nicht an einem Zeichen darin — so
  macht es jede Tabellenkalkulation. Und 26 × 100 `Y.Text` wären
  zweitausendsechshundert eingehängte Yjs-Typen, jeder im verdichteten
  Stand.

  Der eigentliche Gewinn liegt woanders: Eine Formel umzuschreiben ist so
  ein einziges `zellen.set(...)` und kann sich nicht mit fremdem Tippen
  verschränken.
*/

export const FORMAT_STANDARD = "zahl";
export const BREITE_STANDARD = "normal";

/* Womit eine frische Tabelle anfängt. Genug für einen Haushaltsantrag,
   wenig genug, dass das Blatt aufs iPad passt. */
export const SPALTEN_AM_ANFANG = 6;
export const ZEILEN_AM_ANFANG = 20;

/*
  Kennungen, die kein zweites Gerät zufällig auch vergibt — dieselbe
  Überlegung wie bei den Folien. Kurz gehalten, weil zwei davon in
  `notizen.absatz` passen müssen; das Feld fasst 64 Zeichen.
*/
function neueId(vorsatz) {
  const zufall = Math.random().toString(36).slice(2, 8);
  return `${vorsatz}-${Date.now().toString(36)}${zufall}`;
}

export function spaltenVon(doc) {
  return doc.getArray("spalten");
}

export function zeilenVon(doc) {
  return doc.getArray("zeilen");
}

export function zellenVon(doc) {
  return doc.getMap("zellen");
}

function baueSpalte(reihenfolge, format = FORMAT_STANDARD) {
  const map = new Y.Map();
  map.set("id", neueId("sp"));
  map.set("reihenfolge", reihenfolge);
  map.set("format", format);
  map.set("breite", BREITE_STANDARD);
  return map;
}

function baueZeile(reihenfolge) {
  const map = new Y.Map();
  map.set("id", neueId("z"));
  map.set("reihenfolge", reihenfolge);
  return map;
}

/*
  Sortiert wird nach der ZAHL, nicht nach der Stelle im Yjs-Feld.

  Der Grund steht bei `verschiebeZeile`: Umsortieren im Feld selbst würde
  die Einträge neu aufbauen und damit jeden Kommentar zerreißen, der an
  einer Zelle dieser Zeile hängt.
*/
function sortiert(feld) {
  return feld
    .toArray()
    .map((eintrag, stelle) => ({ eintrag, stelle }))
    .sort(
      (a, b) =>
        (a.eintrag.get("reihenfolge") ?? a.stelle) -
        (b.eintrag.get("reihenfolge") ?? b.stelle)
    )
    .map(({ eintrag }) => eintrag);
}

export function sortierteSpalten(doc) {
  return sortiert(spaltenVon(doc));
}

export function sortierteZeilen(doc) {
  return sortiert(zeilenVon(doc));
}

/*
  Das Startgerüst.

  Legt NUR an, wer die Tabelle angelegt hat, und erst, wenn die Sitzung
  bereit ist — beides teuer gelernt bei den Präsentationen: Vor „bereit"
  ginge nichts hinaus und bliebe im eigenen Browser, und legten zwei Leute
  gleichzeitig an, stünde alles doppelt da.

  Die erste Spalte ist Text: Dort stehen die Positionen eines
  Haushaltsantrags, und „Radwege" ist keine Zahl.
*/
export function stelleGeruestSicher(doc) {
  const spalten = spaltenVon(doc);
  const zeilen = zeilenVon(doc);
  if (spalten.length > 0 || zeilen.length > 0) return;

  doc.transact(() => {
    const neueSpalten = [];
    for (let i = 0; i < SPALTEN_AM_ANFANG; i += 1) {
      neueSpalten.push(baueSpalte(i, i === 0 ? "text" : FORMAT_STANDARD));
    }
    spalten.push(neueSpalten);

    const neueZeilen = [];
    for (let i = 0; i < ZEILEN_AM_ANFANG; i += 1) neueZeilen.push(baueZeile(i));
    zeilen.push(neueZeilen);
  });
}

/* ---------------------------------------------------------------
   Zellen
   --------------------------------------------------------------- */

export function liesZelle(doc, spalteId, zeileId) {
  return zellenVon(doc).get(zellschluessel(spalteId, zeileId)) ?? "";
}

/*
  Eine Zelle schreiben. Leer heißt: den Eintrag entfernen, nicht eine leere
  Zeichenkette hinterlassen — sonst wüchse das Dokument mit jeder Zelle,
  die jemand einmal angefasst und wieder geleert hat.
*/
export function schreibeZelle(doc, spalteId, zeileId, inhalt) {
  const schluessel = zellschluessel(spalteId, zeileId);
  const zellen = zellenVon(doc);
  const sauber = typeof inhalt === "string" ? inhalt.trim() : "";

  doc.transact(() => {
    if (sauber === "") zellen.delete(schluessel);
    else zellen.set(schluessel, sauber);
  });
}

/* ---------------------------------------------------------------
   Zeilen und Spalten
   --------------------------------------------------------------- */

/*
  Die Zahl zwischen zwei Nachbarn — genau wie bei den Folien. So muss beim
  Einfügen kein anderer Eintrag angefasst werden, und zwei Leute, die
  gleichzeitig einfügen, treten sich nicht auf die Füße.
*/
function zahlFuerStelle(liste, stelle) {
  const zahlVon = (eintrag) => eintrag.get("reihenfolge") ?? 0;
  const davor = stelle > 0 ? zahlVon(liste[stelle - 1]) : null;
  const danach = stelle < liste.length ? zahlVon(liste[stelle]) : null;

  if (davor === null && danach === null) return 0;
  if (davor === null) return danach - 1;
  if (danach === null) return davor + 1;
  return (davor + danach) / 2;
}

/*
  Alle Formeln im Blatt an eine Änderung der Struktur anpassen.

  ---------------------------------------------------------------
  Die Regel, an der alles hängt
  ---------------------------------------------------------------

  Das läuft NUR auf dem Gerät, das einfügt oder löscht, und in DERSELBEN
  Transaktion. Würde stattdessen jeder Browser beim Eintreffen der Änderung
  umschreiben, verschöbe sich jede Formel bei zwei Teilnehmern doppelt:
  `=SUMME(B2:B4)` würde erst `B3:B5` und dann `B4:B6`. Die Summe wäre
  falsch, das Blatt sähe heil aus, und niemand käme darauf, warum.
*/
function passeFormelnAn(zellen, aenderung) {
  for (const [schluessel, inhalt] of zellen.entries()) {
    if (typeof inhalt !== "string" || !inhalt.startsWith("=")) continue;
    const neu = verschiebeFormel(inhalt, aenderung);
    if (neu !== inhalt) zellen.set(schluessel, neu);
  }
}

export function fuegeZeileEin(doc, stelle) {
  const zeilen = zeilenVon(doc);
  const liste = sortierteZeilen(doc);
  const ziel = Math.max(0, Math.min(stelle, liste.length));

  doc.transact(() => {
    zeilen.push([baueZeile(zahlFuerStelle(liste, ziel))]);
    passeFormelnAn(zellenVon(doc), {
      achse: "zeile",
      art: "eingefuegt",
      stelle: ziel,
    });
  });
}

export function fuegeSpalteEin(doc, stelle) {
  const spalten = spaltenVon(doc);
  const liste = sortierteSpalten(doc);
  const ziel = Math.max(0, Math.min(stelle, liste.length));

  doc.transact(() => {
    spalten.push([baueSpalte(zahlFuerStelle(liste, ziel))]);
    passeFormelnAn(zellenVon(doc), {
      achse: "spalte",
      art: "eingefuegt",
      stelle: ziel,
    });
  });
}

/*
  Eine Zeile löschen — samt ihrer Zellen.

  Die Zellen müssen ausdrücklich weg: Sie hängen an der Kennung der Zeile,
  nicht an ihrer Stelle, und blieben sonst als unerreichbarer Ballast im
  Dokument liegen. Bei jeder gelöschten Zeile ein bisschen mehr.
*/
export function loescheZeile(doc, stelle) {
  const liste = sortierteZeilen(doc);
  /* Die letzte Zeile bleibt — ein Blatt ohne Zeile hätte nichts, worin man
     tippen könnte, und man käme nicht wieder hinein. */
  if (liste.length <= 1) return;
  const ziel = liste[stelle];
  if (!ziel) return;

  const zeilenFeld = zeilenVon(doc);
  const imFeld = zeilenFeld.toArray().indexOf(ziel);
  if (imFeld === -1) return;
  const zeileId = ziel.get("id");

  doc.transact(() => {
    zeilenFeld.delete(imFeld, 1);
    entferneZellenVon(zellenVon(doc), (teile) => teile.zeileId === zeileId);
    passeFormelnAn(zellenVon(doc), {
      achse: "zeile",
      art: "geloescht",
      stelle,
    });
  });
}

export function loescheSpalte(doc, stelle) {
  const liste = sortierteSpalten(doc);
  if (liste.length <= 1) return;
  const ziel = liste[stelle];
  if (!ziel) return;

  const spaltenFeld = spaltenVon(doc);
  const imFeld = spaltenFeld.toArray().indexOf(ziel);
  if (imFeld === -1) return;
  const spalteId = ziel.get("id");

  doc.transact(() => {
    spaltenFeld.delete(imFeld, 1);
    entferneZellenVon(zellenVon(doc), (teile) => teile.spalteId === spalteId);
    passeFormelnAn(zellenVon(doc), {
      achse: "spalte",
      art: "geloescht",
      stelle,
    });
  });
}

function entferneZellenVon(zellen, trifftZu) {
  /* Erst sammeln, dann löschen — während des Löschens über die Schlüssel zu
     laufen, überspringt Einträge. */
  const weg = [];
  for (const schluessel of zellen.keys()) {
    const teile = teileSchluessel(schluessel);
    if (teile && trifftZu(teile)) weg.push(schluessel);
  }
  for (const schluessel of weg) zellen.delete(schluessel);
}

/*
  Eine Zeile an eine andere Stelle schieben.

  Wie bei den Folien: Der Eintrag bleibt, wo er im Yjs-Feld steht, und
  bekommt nur eine neue Zahl. Ein Neuaufbau würde die Kennung ändern — und
  damit jeden Kommentar zerreißen, der an einer Zelle dieser Zeile hängt.

  Formeln werden dabei NICHT angepasst. Das ist Absicht: Beim Verschieben
  bleibt jede Zeile vollständig erhalten, nur die Reihenfolge ändert sich.
  Wer `=SUMME(B2:B4)` geschrieben hat, meinte einen Bereich des Blattes, und
  der ist danach immer noch dort. Excel macht es genauso.
*/
export function verschiebeZeile(doc, stelle, nachStelle) {
  const liste = sortierteZeilen(doc);
  if (stelle === nachStelle) return;
  const ziel = liste[stelle];
  if (!ziel) return;

  const ohne = liste.filter((_, i) => i !== stelle);
  const hin = Math.max(0, Math.min(nachStelle, ohne.length));
  doc.transact(() => ziel.set("reihenfolge", zahlFuerStelle(ohne, hin)));
}

export function verschiebeSpalte(doc, stelle, nachStelle) {
  const liste = sortierteSpalten(doc);
  if (stelle === nachStelle) return;
  const ziel = liste[stelle];
  if (!ziel) return;

  const ohne = liste.filter((_, i) => i !== stelle);
  const hin = Math.max(0, Math.min(nachStelle, ohne.length));
  doc.transact(() => ziel.set("reihenfolge", zahlFuerStelle(ohne, hin)));
}

export function setzeSpaltenformat(doc, stelle, format) {
  const spalte = sortierteSpalten(doc)[stelle];
  if (!spalte) return;
  doc.transact(() => spalte.set("format", format));
}

export function setzeSpaltenbreite(doc, stelle, breite) {
  const spalte = sortierteSpalten(doc)[stelle];
  if (!spalte) return;
  doc.transact(() => spalte.set("breite", breite));
}

/*
  Das ganze Blatt als schlichte Werte — für Rechnen, Drucken und CSV.

  Ab hier sieht niemand mehr Yjs. Genau das macht den Rechner ohne Netz und
  ohne Oberfläche prüfbar.
*/
export function alsGitter(doc) {
  const spalten = sortierteSpalten(doc).map((s) => ({
    id: s.get("id"),
    format: s.get("format") || FORMAT_STANDARD,
    breite: s.get("breite") || BREITE_STANDARD,
  }));
  const zeilen = sortierteZeilen(doc).map((z) => ({ id: z.get("id") }));

  const zellen = new Map();
  for (const [schluessel, inhalt] of zellenVon(doc).entries()) {
    if (typeof inhalt === "string" && inhalt !== "") zellen.set(schluessel, inhalt);
  }

  return { spalten, zeilen, zellen };
}

/*
  Das ganze Blatt ersetzen — für das Einlesen einer CSV-Datei.

  In EINER Transaktion: ein Rückgängig-Schritt, ein Netzpaket. Zeile für
  Zeile zu ersetzen ergäbe Hunderte Pakete und für die anderen ein
  Flackern, bei dem die Tabelle halb alt und halb neu dasteht.
*/
export function ersetzeBlatt(doc, reihen, formate = []) {
  const breite = Math.max(1, ...reihen.map((r) => r.length));
  const hoehe = Math.max(1, reihen.length);

  doc.transact(() => {
    const spalten = spaltenVon(doc);
    const zeilen = zeilenVon(doc);
    const zellen = zellenVon(doc);

    spalten.delete(0, spalten.length);
    zeilen.delete(0, zeilen.length);
    for (const schluessel of [...zellen.keys()]) zellen.delete(schluessel);

    const neueSpalten = [];
    for (let i = 0; i < breite; i += 1) {
      neueSpalten.push(baueSpalte(i, formate[i] || FORMAT_STANDARD));
    }
    spalten.push(neueSpalten);

    const neueZeilen = [];
    for (let i = 0; i < hoehe; i += 1) neueZeilen.push(baueZeile(i));
    zeilen.push(neueZeilen);

    reihen.forEach((reihe, z) => {
      reihe.forEach((inhalt, s) => {
        const sauber = typeof inhalt === "string" ? inhalt.trim() : "";
        if (sauber === "") return;
        zellen.set(
          zellschluessel(neueSpalten[s].get("id"), neueZeilen[z].get("id")),
          sauber
        );
      });
    });
  });
}
